import { useLayoutEffect } from "react";

const VIRTUAL_SCROLLER_SELECTOR = ".MuiDataGrid-virtualScroller";
const COLUMN_HEADERS_INNER_SELECTOR = ".MuiDataGrid-columnHeadersInner";

const isRtlDocument = () =>
  typeof document !== "undefined" &&
  document.documentElement.getAttribute("dir") === "rtl";

/**
 * Chrome reports `scrollLeft` under RTL as either negative or offset from the
 * right edge, depending on the scroll position. Normalise both to the
 * left-origin offset the header transform expects.
 */
const getHorizontalScrollOffset = (scroller) => {
  const { scrollLeft, scrollWidth, clientWidth } = scroller;
  const maxScroll = Math.max(0, scrollWidth - clientWidth);

  if (scrollLeft < 0) {
    return scrollLeft;
  }

  if (maxScroll > 0 && scrollLeft > 0) {
    return scrollLeft - maxScroll;
  }

  return scrollLeft;
};

/**
 * Keeps the column headers aligned with the cells while scrolling horizontally
 * in RTL, where the DataGrid gets it wrong — v5 has no RTL handling in its
 * header-transform path.
 *
 * LTR is deliberately left alone: the grid already positions the header inner
 * element itself, and its offset accounts for column virtualisation
 * (`scrollLeft - columnPositions[firstColumnToRender]`). Overriding that with a
 * raw `scrollLeft` transform misaligns the headers once the grid has scrolled
 * past the column buffer, so in LTR this hook writes nothing at all.
 */
export function useDataGridHeaderScrollSync(containerRef, deps = []) {
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return undefined;
    }

    let virtualScroller = null;
    let columnHeadersInner = null;
    let resizeObserver = null;
    let mountObserver = null;
    let frameId = 0;
    let isDisposed = false;
    let hasCustomTransform = false;

    const syncHeaderScroll = () => {
      if (isDisposed || !virtualScroller || !columnHeadersInner) {
        return;
      }

      // Checked here rather than at setup: `document.dir` is applied in an
      // App-level effect that runs after this layout effect, so the document
      // still reads as LTR while the grid is mounting.
      if (!isRtlDocument()) {
        return;
      }

      const offset = getHorizontalScrollOffset(virtualScroller);
      const nextTransform = `translate3d(${-offset}px, 0px, 0px)`;

      if (columnHeadersInner.style.transform !== nextTransform) {
        columnHeadersInner.style.setProperty(
          "transform",
          nextTransform,
          "important"
        );
        hasCustomTransform = true;
      }
    };

    // Second frame catches the offset once the grid has settled its own layout.
    const scheduleSync = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        syncHeaderScroll();
        frameId = requestAnimationFrame(syncHeaderScroll);
      });
    };

    const detach = () => {
      virtualScroller?.removeEventListener("scroll", scheduleSync);
      resizeObserver?.disconnect();
      resizeObserver = null;
      // Only clear a transform this hook wrote — in LTR the property holds the
      // grid's own inline transform, and removing it would unalign the headers.
      if (hasCustomTransform) {
        columnHeadersInner?.style.removeProperty("transform");
        hasCustomTransform = false;
      }
    };

    const attach = () => {
      virtualScroller = root.querySelector(VIRTUAL_SCROLLER_SELECTOR);
      columnHeadersInner = root.querySelector(COLUMN_HEADERS_INNER_SELECTOR);

      if (!virtualScroller || !columnHeadersInner) {
        return false;
      }

      virtualScroller.addEventListener("scroll", scheduleSync, {
        passive: true,
      });
      scheduleSync();

      // The RTL offset depends on scrollWidth/clientWidth, so resizing the grid
      // changes it even when the scroll position has not moved.
      resizeObserver = new ResizeObserver(scheduleSync);
      resizeObserver.observe(virtualScroller);

      return true;
    };

    if (!attach()) {
      mountObserver = new MutationObserver(() => {
        if (attach()) {
          mountObserver?.disconnect();
          mountObserver = null;
        }
      });
      mountObserver.observe(root, { childList: true, subtree: true });
    }

    return () => {
      isDisposed = true;
      cancelAnimationFrame(frameId);
      mountObserver?.disconnect();
      detach();
    };
  }, [containerRef, ...deps]);
}
